import { useEffect, useId, useState } from 'react'
import { billingApi, type Package } from '@/api/billing'
import { errorMessage } from '@/api/client'
import { presentationCSS, packageColorKeys as keys, packageColorLabels as labels, type Colors, type PackageColors as Settings, type Presentation } from './storefront/presentation'
import { usePackageVisibility } from '@/lib/packageVisibility'
import { RechargePackages } from './storefront/RechargePackages'
import './storefront/preview.css'
import CampaignSettings from './storefront/CampaignSettings'
export default function PackagePreview({ items, localPreview = false }: { items: Package[]; localPreview?: boolean }) {
  const display = usePackageVisibility()
  const visible = display.visible(items)
  const [draft, setDraft] = useState<Presentation>({version:0})
  const [saved, setSaved] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [loadError, setLoadError] = useState('')
  const settings = draft.packageColors || {}
  const [selected, setSelected] = useState('')
  const [colorsOpen, setColorsOpen] = useState(false)
  const [notice, setNotice] = useState('')
  const scope = 'package-preview-' + useId().replace(/:/g, '')
  const selectedPackage = visible.find(p => p.code === selected) || visible[0]
  const colors = settings[selectedPackage?.code] || {}
  const defaults: Required<Colors> = { background: selectedPackage?.recommended ? '#232b34' : '#393939', backgroundEnd: selectedPackage?.recommended ? '#141617' : '#1c1c1c', text: '#eeeeee', border: selectedPackage?.recommended ? '#a2d7ee' : '#ffffff', button: '#9bd6e9', buttonText: selectedPackage?.recommended ? '#23323b' : '#9ed0ed' }
  useEffect(() => {
    let active = true
    const load = async () => {
      try {
        const data: Presentation = localPreview ? JSON.parse(localStorage.getItem('ppnew-presentation-demo-v1') || '{"version":0}') : await billingApi.presentation()
        if (active) { setDraft(data); setSaved(JSON.stringify(data)); setLoadError('') }
      } catch (e) { if (active) setLoadError(errorMessage(e)) }
      finally { if (active) setLoading(false) }
    }
    void load()
    return () => { active = false }
  }, [localPreview])
  function change(next: Presentation) { setDraft(next); setNotice('') }
  function save(next: Settings) { change({...draft,packageColors:next}) }
  async function publish() {
    if (loading || saving || loadError || JSON.stringify(draft) === saved) return
    const message = '该保存会直接修改正式页面的展示效果，是否确认修改？'
    if (!window.confirm(localPreview ? `${message}\n\n当前为本地演示，确认后仅保存本地配置，不会修改正式页面。` : message)) return
    setSaving(true); setNotice('')
    try {
      const result = localPreview ? {...draft,version:draft.version+1} : await billingApi.savePresentation(draft)
      if (localPreview) localStorage.setItem('ppnew-presentation-demo-v1',JSON.stringify(result))
      setDraft(result); setSaved(JSON.stringify(result))
      setNotice(localPreview ? '已保存本地演示配置，未修改线上。' : '已保存，用户端重新打开充值页或下次刷新配置后生效。')
    } catch(e) { setNotice(errorMessage(e)) }
    finally { setSaving(false) }
  }
  const css = presentationCSS(scope,draft,visible)
  return <section className="storefront-preview-shell" aria-label="用户端展示预览">
    <div className="storefront-live-layout">
    <div className="storefront-live-controls" aria-label="预览编辑设置">
    <fieldset disabled={loading || saving || !!loadError}><CampaignSettings value={draft} onChange={change} />
<div className="storefront-package-settings-heading"><strong>套餐卡片颜色</strong><p>仅设置下方套餐卡片，独立于顶部活动横幅。</p><button type="button" onClick={() => setColorsOpen(v => !v)}>{colorsOpen ? '收起套餐颜色设置' : '设置套餐卡片颜色'}</button></div>
    {colorsOpen && <div className="storefront-color-settings">
      <label>选择套餐<select disabled={!visible.length} value={selectedPackage?.code || ''} onChange={e => setSelected(e.target.value)}>{!visible.length && <option value="">暂无用户端展示的套餐</option>}{visible.map(p => <option key={p.id || p.code} value={p.code}>{p.name} · {p.code}</option>)}</select></label>
      <p>仅列出用户端实际展示的套餐。卡片起始色与结束色可独立调整；设置为相同颜色即可使用纯色。默认保留线上配色。</p>
      <div className="storefront-color-grid">{keys.map((key, index) => <label key={key}>{labels[index]}<span><input aria-label={labels[index]} type="color" disabled={!selectedPackage} value={colors[key] || defaults[key]} onInput={e => { if (selectedPackage) save({ ...settings, [selectedPackage.code]: { ...colors, [key]: e.currentTarget.value } })}}/><code>{colors[key] || '线上默认'}</code><button type="button" disabled={!colors[key]} onClick={() => { const next = { ...colors }; delete next[key]; save({ ...settings, [selectedPackage.code]: next }) }}>恢复</button></span></label>)}</div>
      <button type="button" disabled={!selectedPackage} onClick={() => { const next = { ...settings }; delete next[selectedPackage.code]; save(next) }}>恢复此套餐线上配色</button>
    </div>}
    </fieldset>
    {notice && <p role="status" className="px-5 py-2 text-xs text-slate-500">{notice}</p>}
    </div>
    <div className="storefront-live-result"><div className="storefront-live-caption">实时预览<span>修改文字或颜色，即时更新</span></div><div id={scope} className="storefront-preview-stage"><style>{css}</style><RechargePackages presentation={draft} plans={items.map(p => ({ ...p, kind: 'topup' }))} busy={false} available={false} onPay={() => {}} onHistory={() => setNotice('此处仅预览套餐，不读取充值记录。')} /></div></div>
    </div>
    <div className="storefront-save-bar"><span role="status">{loading ? '正在读取展示配置…' : loadError || (JSON.stringify(draft)!==saved ? '有未保存的修改' : '展示配置已同步')}{localPreview && ' · 本地演示'}</span><button type="button" disabled={loading || saving || !!loadError || JSON.stringify(draft)===saved} onClick={publish}>{saving ? '正在保存…' : localPreview ? '保存（本地演示）' : '保存并应用到用户端'}</button></div>
  </section>
}





